import { type ISbStoryData, useStoryblokState } from '@storyblok/react'
import type { GetStaticProps, NextPage } from 'next'
import { type FormCategoryProps, type ISbStory } from '~/types'
import { type PageMetadataProps } from '~/components/PageMetadata/PageMetadata.interface'
import { mapPageMetadata } from '~/components/PageMetadata/PageMetadata.mapping'
import { PageMetadata } from '~/components/PageMetadata/PageMetadata'
import { Layout } from '~/components/Layout/Layout'
import { mapLayout } from '~/components/Layout/Layout.mapping'
import {
  type SignUpStoryblok,
  type GlobalSettingsStoryblok
} from '~/types/generated'
import {
  fetchDatasources,
  fetchBusinessesContent,
  fetchAllStoryblokContent
} from '~/lib/storyblok'
import { SignUpPage } from '~/components/SignUpPage/SignUpPage'
import { mapBussinessFinder } from '~/components/BusinessFinder/BusinessFinder.mapping'

export interface SignUpPageProps extends PageMetadataProps {
  story: ISbStoryData<SignUpStoryblok>;
  settings: ISbStory;
  layout: ISbStory;
  categories: FormCategoryProps[];
  finder: ISbStory;
}

const SignUp: NextPage<SignUpPageProps> = (ctx): JSX.Element => {
  const { metadata, story: initialStory, layout, categories, finder } = ctx
  const story = useStoryblokState(initialStory)

  return (
    <>
      <PageMetadata metadata={metadata} />
      <Layout layout={layout} businessFinder={finder} altCTA>
        {story?.content && (
          <SignUpPage story={{ ...story?.content, categories }} />
        )}
      </Layout>
    </>
  )
}

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = 'get-listed'

  const version = context.draftMode ? 'draft' : 'published'

  const getStoryblokStories = await fetchAllStoryblokContent({
    by_slugs: `global-settings,${slug}`,
    resolve_relations: 'globalSettings.footerNavigation',
    version
  })

  const globalSettingsData = getStoryblokStories.find(
    (content: { slug: string }) => content.slug === 'global-settings'
  )
  const pageData = getStoryblokStories.find(
    (content: { slug: string }) => content.slug === slug
  )

  if (!globalSettingsData || !pageData) {
    return { notFound: true }
  }

  const businessesData = await fetchBusinessesContent({
    version: 'published'
  })

  if (!businessesData) {
    return { notFound: true }
  }

  const businessCategories = businessesData.map(
    (bussiness: ISbStory) => bussiness.content.businessCategory[0]
  )

  const finder = mapBussinessFinder(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    businessCategories,
    globalSettingsData.content as GlobalSettingsStoryblok
  )

  const fullSlug = pageData.full_slug as string
  const metadata = mapPageMetadata(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    pageData.content.seoMetadata[0],
    fullSlug,
    globalSettingsData.content as GlobalSettingsStoryblok
  )

  const layout = mapLayout(
    globalSettingsData.content as GlobalSettingsStoryblok,
    false
  )

  const categories = await fetchDatasources()
  // console.log("Categories", categories)

  return {
    props: {
      metadata,
      story: pageData ?? false,
      layout,
      finder,
      categories: categories ?? []
    },
    revalidate: 3600 // revalidate every hour
  }
}

export default SignUp
